import { Link } from 'react-router-dom';
import type { LimitExceededPayload, Model } from '../api/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

// CHANGE_01/02 metrics. Feature gates (advanced_diff/branching/merge) and storage
// arrive with their own never-hostage copy below; this maps the LimitExceeded
// `metric` to a human phrase.
const METRIC_LABELS: Record<string, string> = {
  max_active_projects: 'active projects',
  exports_per_month: 'exports this month',
  max_active_share_links: 'active share links',
  storage: 'storage',
  max_doc_bytes: 'document size',
  advanced_diff: 'advanced visual diff',
  branching: 'branching',
  merge: 'assisted merge',
  max_seats: 'team seats',
  custom_templates: 'custom print templates',
  max_templates_per_account: 'saved templates',
};

// Feature-gate metrics carry a Pro/Team teaser instead of a "used/limit" line.
const FEATURE_GATES: Record<string, string> = {
  advanced_diff: 'Graphical, per-entity diff is a Pro feature.',
  branching: 'Branching is a Pro feature.',
  merge: 'Assisted merge is a Team feature.',
  custom_templates: 'Importing your own company print template is a Pro feature.',
};

function metricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric.replace(/_/g, ' ');
}

function resetText(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

export interface ArchiveSwap {
  models: Model[];
  busy: boolean;
  actionLabel: string;
  onArchive: (id: string) => void;
}

// The single, polished freemium-conversion surface (now a Radix Dialog: focus
// trap, Esc-to-close, scrim — first-class a11y). It consumes a LimitExceeded
// payload verbatim and, for the active-models wall, offers the archive-swap the
// product rules call for. No Stripe yet (E2) — "View plans" points at the page.
export default function UpgradePrompt({
  payload,
  onClose,
  archiveSwap,
}: {
  payload: LimitExceededPayload;
  onClose: () => void;
  archiveSwap?: ArchiveSwap;
}) {
  const reset = resetText(payload.window_resets_at);
  const isFeatureGate = payload.metric in FEATURE_GATES;
  const showSwap =
    payload.metric === 'max_active_projects' && archiveSwap && archiveSwap.models.length > 0;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isFeatureGate ? 'Upgrade to unlock this' : 'You’ve reached your plan limit'}</DialogTitle>
          <DialogDescription>
            {isFeatureGate
              ? FEATURE_GATES[payload.metric]
              : `You’ve used ${payload.used} of ${payload.limit} ${metricLabel(payload.metric)} on your current plan.${reset ? ` This resets on ${reset}.` : ''}`}
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-foreground">{payload.upgrade_hint}</p>

        {showSwap ? (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Free a slot by archiving
            </p>
            <ul className="max-h-44 space-y-1 overflow-auto">
              {archiveSwap.models.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <span className="truncate text-sm text-foreground">{m.name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={archiveSwap.busy}
                    onClick={() => archiveSwap.onArchive(m.id)}
                  >
                    {archiveSwap.actionLabel}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Not now
          </Button>
          <Button asChild>
            <Link to="/plans">View plans</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
